import ComponentElement from 'components/component-element';
import ModalLayers from 'developer_components/modalLayers';
import {createElement, height, getTransitionDuration} from 'methods/utils';

/**
 * camelCase текст в kebab-case
 * @param  {String} str camelCase строка
 * @return {String}     kebab-case строка
 */
function camelCaseToKebabCase(str) {
    return str.replace(/[A-Z]+/g, (match, position) => {
        return position ? '-' + match.toLowerCase() : match
    });
}

function Modal(target, options = {}) {
    if (target?.nodeType) {
        // передан элемент
        this.$element = target;
    } else if (typeof target == 'string') {
        this.$element = document.querySelector(target);
    } else {
        throw new Error('Unknown target');
    }

    // модалка не найдена
    if (!this.$element) {
        throw new Error('Modal not found');
    }

    // ищем инстанс
    if (this.$element.Modal instanceof Modal) {
        return this.$element.Modal;
    }

    // сохраняем себя в модалку
    this.$element.Modal = this;

    // наследуемся
    ComponentElement.call(this, this.$element, options);

    this.$modal = this.$element;

    // добавление класса родителя в классы модалки
    if (typeof this.options.className != 'undefined') {
        this.$element.classList.add(this.options.className);
    } else if (this.constructor != Modal.prototype.constructor) {
        this.$element.classList.add(camelCaseToKebabCase(this.constructor.name));
    }

    // клик по крестику
    const $close = this.$element.querySelectorAll('.Modal-native__close, .js-modalClose');
    if ($close && $close.length) {
        const closeClickEventBind = this._onCloseClickEvent.bind(this);
        $close.forEach($el => {
            $el.addEventListener('click', closeClickEventBind);
        });
    }

    this._$wrapper = this.$element.querySelector('.Modal-native__wrapper');
    if (this.options.disableClose === false) {
        this._$wrapper.addEventListener('click', this._onWrapperClickEvent.bind(this));
        this._$wrapper.addEventListener('mousedown', this._onWrapperMouseDownEvent.bind(this));
    }

    ModalLayers.add(this);

    // если модалка выше чем viewport то даем возможность скроллить
    window.addEventListener('resize', this._onWindowResizeEvent.bind(this));

    this.update();
}

// цепочка прототипов
Modal.prototype = Object.create(ComponentElement.prototype);
Object.defineProperty(Modal, 'constructor', {
    value: Modal,
    enumerable: false,
    writable: true,
});

Modal.prototype.setOptions = function(options) {
    ComponentElement.prototype.setOptions.call(this, Object.assign({
        disableClose: false, // отключает закрытие модалки при клике вне ее
        className: undefined, // имя CSS класса для добавления его в модалку
        backdrop: true, // затемнять задний фон
    }, options));
}

Modal.createFromHTML = function(content, options = {}) {
    return createElement(
        `<div${options.id ? ` id="${options.id}"` : ''} class="Modal-native${options.className ? ' ' + options.className : ''}">
            <div class="Modal-native__wrapper">
                <div class="Modal-native__container">
                    ${content}
                </div>
            </div>
        </div>`
    );
}

Modal.prototype._onWindowResizeEvent = function() {
    this._wrapperHeight = height(this._$wrapper.firstElementChild);
    this._$wrapper.classList.toggle('top', this._wrapperHeight > window.innerHeight);
}

Modal.prototype.update = function() {
    this._onWindowResizeEvent();
}

Modal.prototype.isOpened = function() {
    return this.$element.classList.contains('is-opened');
}

/**
 * Открытие модалки
 */
Modal.prototype.open = function() {
    // от повторных открытий
    if (this.$element.classList.contains('is-active')) {
        return Promise.resolve();
    }

    // оповещаем
    this.emit('before:open');

    // показываем
    this.$element.classList.add('is-active');

    // вешаем id модалки на body
    if (this.options.modal_id) {
        document.body.dataset.modal_id = this.options.modal_id;
    }

    return new Promise((resolve) => {
        window.requestAnimationFrame(() => {
            this.$element.classList.add('is-opened');
            this.update();

            // оповещаем
            this.emit('open');

            setTimeout(() => {
                resolve();
                this.emit('opened');
            }, getTransitionDuration(this.$element))
        });
    });
}

/**
 * Закрытие модалки.
 *
 * @param {String} initiator Инициатор события
 */
 Modal.prototype.close = function(initiator) {
    // от повторных закрытий
    if (!this.$element.classList.contains('is-active')) {
        return Promise.resolve();
    }

    initiator ??= 'user';

    // оповещаем
    this.emit('before:close', initiator);

    // скрываем
    this.$element.classList.remove('is-opened');

    // удаляем id модалки на body
    if (this.options.modal_id) {
        delete document.body.dataset.modal_id;
    }

    return new Promise((resolve) => {
        requestAnimationFrame(() => {
            setTimeout(() => {
                // скрываем
                this.$element.classList.remove('is-active');

                // оповещаем
                resolve();
                this.emit('close', initiator);
            }, getTransitionDuration(this.$element))
        });
    });
}

/**
 * Удаление модалки
 * @param  {Event} e Событие
 */
Modal.prototype.remove = function(e) {
    if (!this.$element) {
        return;
    }

    // оповещаем
    this.emit('before:remove');

    this.$element.remove();

    // оповещаем
    this.emit('remove');
}

Modal.prototype._onCloseClickEvent = function(e) {
    this.close('user');
}

Modal.prototype._onWrapperClickEvent = function (e) {
     if (this._$wrapper == e.target && this._$mouseDownTarget == e.target) {
        this.close('user');
    }

    this._$mouseDownTarget = null;
}

Modal.prototype._onWrapperMouseDownEvent = function (e) {
    this._$mouseDownTarget = e.target;
}

window.Modal = Modal;

export default Modal;
